import React, { useState } from 'react';
import { Box, Button, Input, List, ListItem, Typography, Chip, Stack } from '@mui/joy';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';

interface Domain {
  id: number;
  host: string;
  status: 'pending' | 'verified';
  dns_instructions: {
    cname: { host: string; value: string };
    txt: { host: string; value: string };
  };
}

interface DomainManagerProps {
  siteId?: number;
  value?: string;
  onChange?: (host: string) => void;
}

export const DomainManager: React.FC<DomainManagerProps> = ({ siteId, value, onChange }) => {
  const queryClient = useQueryClient();
  const [newDomain, setNewDomain] = useState(value || '');

  const isOnboarding = !siteId;

  const { data: domains, isLoading } = useQuery({
    queryKey: ['cms-site-domains', siteId],
    queryFn: async () => {
      const response = await api.get<Domain[]>(`/api/cms/sites/${siteId}/domains/`);
      return response.data;
    },
    enabled: !!siteId,
  });

  const addDomainMutation = useMutation({
    mutationFn: async () => {
      if (isOnboarding) {
        return { host: newDomain } as Domain;
      }
      const response = await api.post<Domain>(`/api/cms/sites/${siteId}/domains/`, { host: newDomain });
      return response.data;
    },
    onSuccess: () => {
      if (isOnboarding) {
        onChange && onChange(newDomain);
      } else {
        setNewDomain('');
        queryClient.invalidateQueries({ queryKey: ['cms-site-domains', siteId] });
      }
    },
  });

  const verifyDomainMutation = useMutation({
    mutationFn: async (domainId: number) => {
      const response = await api.post(`/api/cms/sites/${siteId}/domains/${domainId}/verify/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-site-domains', siteId] });
    },
  });

  const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isValid = domainPattern.test(newDomain);

  if (isOnboarding) {
    return (
      <Stack direction="row" spacing={1} mb={2}>
        <Input
          placeholder="example.com"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
        />
        <Button disabled={!isValid} onClick={() => addDomainMutation.mutate()}>Use Domain</Button>
      </Stack>
    );
  }

  if (isLoading) {
    return <Typography>Loading domains...</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={2}>
        <Input
          placeholder="example.com"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
        />
        <Button disabled={!newDomain} onClick={() => addDomainMutation.mutate()}>Add Domain</Button>
      </Stack>

      {domains && domains.length > 0 ? (
        <List>
          {domains.map((domain) => (
            <ListItem key={domain.id} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography level="body-lg">{domain.host}</Typography>
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  Point CNAME to <b>{domain.dns_instructions.cname.value || 'your CloudFront domain'}</b>
                </Typography>
                <Typography level="body-sm">
                  Add TXT record <b>{domain.dns_instructions.txt.value}</b>
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip color={domain.status === 'verified' ? 'success' : 'neutral'}>{domain.status}</Chip>
                {domain.status === 'pending' && (
                  <Button size="sm" onClick={() => verifyDomainMutation.mutate(domain.id)}>
                    Verify
                  </Button>
                )}
              </Stack>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography level="body-sm">No domains added yet.</Typography>
      )}
    </Box>
  );
};

export default DomainManager;
